import t from '~/scopes/campaign/data/metals/rare-earth.translations'
import SECTIONS from '~/scopes/campaign/data/metals/sections.translations'

// Rare earth copy, final research sheet. Citations are keys into the shared bibliography
// (`data/sources.ts`); each key carries the sheet's own number, so what renders matches
// the sheet's brackets exactly. Read-more headings come from the shared taxonomy
// (`sections.translations.ts`); the five slots the sheet marks `n/a` are omitted rather
// than authored empty — biodiversity loss under environmental, and displacement, water
// rights, child labour and gender under human. Human impact is left with two blocks out
// of six, the thinnest read-more of any metal so far, even at a severity of 3.
//
// The sheet's footnotes have since arrived, so both asterisks are reproduced: the `*` on
// "A family of 17 elements" (the client marked the metal name too, but that string renders in
// the library grid, the chips and the map tooltips, none of which parse markdown — one marker
// on the phrase inside the prose is the honest reading of the same note), and the `**` the
// sheet puts beside all three impact scores, declared as slots since those labels are shared
// with the impacts navigation.

export default {
  name: t.name,
  smartphone: {
    label: t.smartphoneLabel,
  },
  whatItDoes: {
    text: t.whatItDoesText,
  },
  extraction: [
    {
      text: t.extractionText1,
      sourceRefs: ['ademe2024', 'pblEnvironmental2024'],
    },
  ],
  quickFact: {
    text: t.quickFactText,
    sourceRefs: ['pblEnvironmental2024'],
  },
  impacts: {
    environmental: {
      summary: {
        text: t.envSummaryText,
        sourceRefs: ['ademe2024', 'pblEnvironmental2024'],
      },
      blocks: [
        {
          title: SECTIONS.environmentalWater,
          text: t.envWaterText,
          sourceRefs: ['pblEnvironmental2024'],
        },
        {
          title: SECTIONS.environmentalSoil,
          text: t.envSoilText,
          sourceRefs: ['ademe2024', 'pblEnvironmental2024'],
        },
        {
          title: SECTIONS.environmentalClimate,
          text: t.envClimateText,
          sourceRefs: ['pblEnvironmental2024'],
        },
      ],
    },
    human: {
      summary: {
        text: t.humanSummaryText,
        sourceRefs: ['ademe2024', 'pblSocial2025'],
      },
      blocks: [
        {
          title: SECTIONS.humanHealth,
          text: t.humanHealthText,
          sourceRefs: ['pblSocial2025'],
        },
        {
          title: SECTIONS.humanLegal,
          text: t.humanLegalText,
          sourceRefs: ['pblSocial2025', 'yaleE360Myanmar2025', 'bhrrc2023'],
        },
      ],
    },
    geopolitical: {
      summary: {
        text: t.geoSummaryText,
        sourceRefs: ['ieaGcmo2025', 'bhrrc2023'],
      },
      blocks: [
        {
          title: SECTIONS.geopoliticalDug,
          text: t.geoDugText,
          sourceRefs: ['usgsMcs2025', 'ieaGcmo2025'],
        },
        {
          title: SECTIONS.geopoliticalProcessed,
          text: t.geoProcessedText,
          sourceRefs: ['ieaGcmo2025'],
        },
        {
          title: SECTIONS.geopoliticalLeverage,
          text: t.geoLeverageText,
          sourceRefs: ['ieaGcmo2025', 'bhrrc2023'],
        },
      ],
    },
  },
  supplyRisk: {
    summary: {
      text: t.supplySummaryText,
      sourceRefs: ['ieaGcmo2025'],
    },
    blocks: [
      {
        title: SECTIONS.supplyRiskReserves,
        text: t.supplyReservesText,
        sourceRefs: ['ieaGcmo2025'],
      },
      {
        title: SECTIONS.supplyRiskDemand,
        text: t.supplyDemandText,
        sourceRefs: ['ieaMineralsExplorer'],
      },
    ],
  },
  recycling: {
    summary: {
      text: t.recyclingSummaryText,
      sourceRefs: ['ieaRecycling2024'],
    },
    blocks: [
      {
        title: SECTIONS.recyclingInput,
        text: t.recyclingInputText,
        sourceRefs: ['ieaRecycling2024'],
      },
      {
        title: SECTIONS.recyclingOutlook,
        text: t.recyclingOutlookText,
        sourceRefs: ['ieaRecycling2024', 'euCrma2024'],
      },
    ],
  },
  footnotes: [
    {
      body: [
        {
          text: t.footnote1Text1,
        },
        {
          text: t.footnote1Text2,
          sourceRefs: ['ademe2024', 'ieaGcmo2025'],
        },
      ],
    },
    {
      // The sheet marks all three impact scores, so each label carries its own `**`
      // rather than one standing for the set.
      slots: ['environmentalScore', 'humanScore', 'geopoliticalScore'],
      body: [
        {
          text: t.footnote2Text1,
        },
        {
          text: t.footnote2Text2,
        },
        {
          text: t.footnote2Text3,
        },
        {
          text: t.footnote2Text4,
        },
      ],
    },
  ],
} as const
